<template>
  <div class="page">
    <main class="content">
      <!-- Hero Section -->
      <section class="landing-hero">
        <h1 class="title">Partycord</h1>
        <p class="subtitle">
          <span class="temporary">Temporary</span> voice channels made 
          <span class="fun">fun</span> and 
          <span class="easy">easy</span>!
        </p>
        <div class="discord-button-container">
          <button @click="inviteBot" class="discord-button">
            <fa icon="plus" />
            Invite Bot
          </button>
          <button @click="joinServer" class="discord-button">
            <fa :icon="['fab', 'discord']" />
            Support Server
          </button>
          <button @click="voteOnTopgg" class="discord-button vote-button">
            <img src="../assets/top gg.png" alt="Top.gg" class="topgg-logo" />
            Vote on Top.gg
          </button>
        </div>
      </section>

      <!-- Patreon Supporters Section -->
      <section class="supporters-section">
        <div class="supporters-header">
          <div class="supporters-icon">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          </div>
          <h2 class="supporters-title">Our Amazing Supporters</h2>
          <p class="supporters-description">$1 goes a long way in helping us improve Partycord.</p>
          <br>
          <p class="supporters-description">Thank you to everyone who helps make Partycord possible!</p>
        </div>
        
        <div 
          class="supporters-grid" 
          :class="{ 'fading': isTransitioning }"
          role="group" 
          aria-label="Patreon supporters"
        >
          <div 
            v-for="supporter in displayedSupporters" 
            :key="supporter.id" 
            class="supporter-card"
            :class="getTierClass(supporter.tier)"
            tabindex="0"
          >
            <div class="supporter-card-inner">
              <div class="supporter-avatar-wrapper">
                <div 
                  class="supporter-avatar"
                  :style="{ background: `linear-gradient(135deg, ${getSupporterColor(supporter)}, ${getSupporterColor(supporter)}80)` }"
                >
                  <img 
                    v-if="supporter.avatar" 
                    :src="supporter.avatar" 
                    :alt="`${supporter.name} avatar`"
                    class="supporter-image"
                    loading="lazy"
                  >
                  <span v-else class="supporter-initial">{{ getSupporterInitial(supporter.name) }}</span>
                </div>
                <div class="supporter-glow"></div>
              </div>
              <div class="supporter-info">
                <h3 class="supporter-name">{{ supporter.name }}</h3>
                <span class="supporter-tier-badge">
                  <span class="tier-icon" :class="getTierIconClass(supporter.tier)"></span>
                  {{ supporter.tier }}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="patreon-cta">
          <p class="cta-text">Want to support Partycord?</p>
          <div class="support-buttons">
            <button @click="openPatreon" class="patreon-button">
              <svg class="patreon-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M0 .48v23.04h4.22V.48zm15.385 0c-4.764 0-8.641 3.88-8.641 8.65 0 4.755 3.877 8.623 8.641 8.623 4.75 0 8.615-3.868 8.615-8.623C24 4.36 20.136.48 15.385.48z"/>
              </svg>
              Become a Patron
              <span class="button-shine"></span>
            </button>
            <button @click="voteOnTopgg" class="patreon-button topgg-support-button">
              <img src="../assets/top gg.png" alt="Top.gg" class="topgg-icon" />
              Vote on Top.gg (Free!)
              <span class="button-shine"></span>
            </button>
          </div>
          <p class="support-subtitle">Support us with $1/month or vote for free – every bit helps!</p>
        </div>
      </section>

      <!-- Features Section -->
      <section class="features">
        <article class="feature feature1">
          <div class="feature-content">
            <h2 class="feature-title">Control Panel</h2>
            <p class="feature-description">Easily control your voice channel & its connected members from a click of a button!</p>
          </div>
          <div class="feature-image">
            <img 
              src="../assets/control_panel.png" 
              alt="Control Panel interface showing voice channel management buttons"
              loading="lazy"
            >
          </div>
        </article>

        <article class="feature feature2 feature-reverse">
          <div class="feature-content">
            <h2 class="feature-title">User Management</h2>
            <p class="feature-description">Control who can see and join your private voice channels with intuitive user management tools.</p>
          </div>
          <div class="feature-image">
            <img 
              src="../assets/user_management2.png" 
              alt="User management interface showing permission controls"
              loading="lazy"
            >
          </div>
        </article>

        <article class="feature feature3">
          <div class="feature-content">
            <h2 class="feature-title">Channel Settings</h2>
            <p class="feature-description">Change your channel's settings to fit your liking. There is even a menu if you don't want to change one setting at a time! If you like your settings, you can also set them as your default for next time.</p>
          </div>
          <div class="feature-image">
            <img 
              src="../assets/channel_management.png" 
              alt="Channel settings interface showing various configuration options"
              loading="lazy"
            >
          </div>
        </article>
      </section>

      <!-- Staff Section -->
      <section class="staff-section">
        <h2 class="staff-title">Meet the Team</h2>
        <div class="staff-grid">
          <div
            v-for="member in staffMembers"
            :key="member.handle"
            :class="['staff-card', `staff-card--${member.cardType}`]"
          >
            <img 
              class="staff-avatar" 
              :src="member.avatarUrl"
              :alt="`${member.displayName} profile picture`"
              @error="onStaffAvatarError"
              :data-fallback-avatar="member.fallbackAvatar"
              loading="lazy"
            >
            <h3 class="staff-name">{{ member.handle }}</h3>
            <p class="staff-role">{{ member.role }}</p>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import supportersData from '@/data/supporters.json';
import legacySupportersData from '@/data/legacySupporters.json';
import sleeplessAvatarFallback from '@/assets/SleeplessPFP.png';
import silverAvatarFallback from '@/assets/SilverPFP.png';

function getSupporterMergeKey(supporter) {
  if (supporter.discordId) {
    return `discord:${supporter.discordId}`;
  }

  if (supporter.patreonMemberId) {
    return `patreon:${supporter.patreonMemberId}`;
  }

  return `name:${(supporter.name || '').toLowerCase()}|tier:${(supporter.tier || '').toLowerCase()}`;
}

function mergeSupporterSources(primarySupporters, fallbackSupporters) {
  const merged = new Map();

  fallbackSupporters.forEach((supporter) => {
    merged.set(getSupporterMergeKey(supporter), supporter);
  });

  primarySupporters.forEach((supporter) => {
    merged.set(getSupporterMergeKey(supporter), supporter);
  });

  return [...merged.values()];
}

export default {
  name: 'LandingPage',
  data() {
    const staffMembers = [
      {
        handle: '@sleeplesskyru',
        displayName: 'Sleepless Kyru',
        role: 'Developer',
        cardType: 'developer',
        discordUserId: '600175064024875008',
        fallbackAvatar: sleeplessAvatarFallback
      },
      {
        handle: '@silverhood',
        displayName: 'Silverhood',
        role: 'Feature Designer',
        cardType: 'designer',
        discordUserId: '474186115939696640',
        fallbackAvatar: silverAvatarFallback
      }
    ];

    return {
      allSupporters: mergeSupporterSources(supportersData, legacySupportersData),
      displayedSupporters: [],
      staffMembers: staffMembers.map((member) => ({
        ...member,
        avatarUrl: member.fallbackAvatar
      })),
      maxDisplayed: 6, // Number of supporters to show at once
      cycleInterval: null,
      isTransitioning: false,
      isLoading: false,
      observer: null,
      logoLoaded: false
    };
  },
  mounted() {
    this.randomizeSupporters();
    this.startAutoCycle();
    this.setupIntersectionObserver();
    this.refreshStaffAvatars();
  },
  beforeUnmount() {
    if (this.cycleInterval) {
      clearInterval(this.cycleInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  emits: ['invite-bot', 'join-server'],
  methods: {
    inviteBot() {
      this.$emit('invite-bot');
    },
    joinServer() {
      this.$emit('join-server');
    },
    voteOnTopgg() {
      window.open('https://top.gg/bot/756856376201969716/vote', '_blank');
    },
    openPatreon() {
      window.open('https://www.patreon.com/partycord', '_blank');
    },
    async fetchDiscordAvatarUrl(discordUserId) {
      const response = await fetch(`https://japi.rest/discord/v1/user/${encodeURIComponent(discordUserId)}`);
      if (!response.ok) {
        throw new Error(`Avatar lookup failed (${response.status})`);
      }

      const payload = await response.json();
      const avatarUrl = payload?.data?.avatarURL || payload?.data?.avatar?.url || payload?.data?.avatar || null;

      if (!avatarUrl || typeof avatarUrl !== 'string') {
        return null;
      }

      return avatarUrl;
    },
    async refreshStaffAvatars() {
      const membersWithAvatars = await Promise.all(
        this.staffMembers.map(async (member) => {
          try {
            const avatarUrl = await this.fetchDiscordAvatarUrl(member.discordUserId);

            return {
              ...member,
              avatarUrl: avatarUrl || member.fallbackAvatar
            };
          } catch (error) {
            return member;
          }
        })
      );

      this.staffMembers = membersWithAvatars;
    },
    onStaffAvatarError(event) {
      const image = event?.target;

      if (!image || image.dataset.fallbackApplied === 'true') {
        return;
      }

      const fallbackAvatar = image.dataset.fallbackAvatar;
      if (!fallbackAvatar) {
        return;
      }

      image.dataset.fallbackApplied = 'true';
      image.src = fallbackAvatar;
    },
    getSupporterInitial(name) {
      if (!name) {
        return '?';
      }
      return name.trim().charAt(0);
    },
    getSupporterColor(supporter) {
      const normalizedTier = (supporter?.tier || '').toLowerCase().trim();
      const tierColorMap = {
        confetti: '#10c5f2',
        partygoer: '#7289da',
        pertygoer: '#7289da',
        founder: '#10f5a0',
        'garden keeper': '#ff4e1d',
        special: '#ff4e1d'
      };

      return tierColorMap[normalizedTier] || supporter?.color || '#ff424d';
    },
    getTierClass(tier) {
      const normalizedTier = (tier || '').toLowerCase().trim();

      if (normalizedTier === 'garden keeper' || normalizedTier === 'special') {
        return 'tier-garden-keeper';
      }

      if (normalizedTier === 'partygoer' || normalizedTier === 'pertygoer') {
        return 'tier-partygoer';
      }

      if (normalizedTier === 'founder') {
        return 'tier-founder';
      }

      if (normalizedTier === 'confetti') {
        return 'tier-confetti';
      }

      return `tier-${normalizedTier}`;
    },
    getTierIconClass(tier) {
      const normalizedTier = (tier || '').toLowerCase().trim();

      if (normalizedTier === 'garden keeper' || normalizedTier === 'special') {
        return 'tier-icon-bloom';
      }

      if (normalizedTier === 'founder') {
        return 'tier-icon-crown';
      }

      if (normalizedTier === 'partygoer' || normalizedTier === 'pertygoer') {
        return 'tier-icon-diamond';
      }

      return '';
    },
    randomizeSupporters() {
      // Shuffle the array and take the first maxDisplayed items
      const shuffled = [...this.allSupporters].sort(() => Math.random() - 0.5);
      this.displayedSupporters = shuffled.slice(0, this.maxDisplayed);
    },
    startAutoCycle() {
      this.cycleInterval = setInterval(() => {
        this.cycleSupporters();
      }, 6000); // Change every 6 seconds
    },
    async cycleSupporters() {
      this.isTransitioning = true;
      
      // Wait for fade out
      setTimeout(() => {
        this.randomizeSupporters();
        this.isTransitioning = false;
      }, 500); // Half second for fade transition
    },
    setupIntersectionObserver() {
      // Only setup animations if user hasn't requested reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const options = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            entry.target.classList.remove('will-animate');
          }
        });
      }, options);

      // Add animation classes only to elements that are not initially visible
      this.$nextTick(() => {
        const animatedElements = this.$el.querySelectorAll('.fade-in-up, .feature, .staff-card, .supporter-card');
        
        animatedElements.forEach(el => {
          // Check if element is in viewport initially
          const rect = el.getBoundingClientRect();
          const isInitiallyVisible = rect.top < window.innerHeight && rect.bottom > 0;
          
          if (!isInitiallyVisible) {
            // Only animate elements that are initially out of view
            el.classList.add('will-animate');
            this.observer.observe(el);
          }
        });
      });
    }
  }
}
</script>

<style scoped>
/* Hero Section */
.landing-hero {
  text-align: center;
  padding: var(--spacing-xxl) 0;
  margin-bottom: var(--spacing-xl);
}

.title {
  font-size: clamp(3rem, 10vw, 6rem);
  margin-bottom: var(--spacing-lg);
  background: var(--color-gradient-primary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% 200%;
  animation: gradientPulse 3s ease-in-out infinite;
  line-height: 1.22;
}

@keyframes gradientPulse {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.discord-button-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  margin: var(--spacing-lg) auto;
  width: 100%;
}

.vote-button {
  background: linear-gradient(135deg, #FF396B, #1D1B26) !important;
  position: relative;
  overflow: hidden;
}

.vote-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.vote-button:hover::before {
  left: 100%;
}

.vote-button:hover {
  background: linear-gradient(135deg, #FF5080, #FF396B) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(255, 57, 107, 0.4);
}

.topgg-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.subtitle {
  color: var(--color-text-secondary);
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  margin-bottom: var(--spacing-xl);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4;
}

.temporary {
  color: #00b7ff;
  font-weight: 600;
}

.fun {
  color: #8e97fa;
  font-weight: 600;
}

.easy {
  color: #fe5ea1;
  font-weight: 600;
}

/* Logo */
.logo {
  width: min(400px, 70vw);
  height: auto;
  margin: var(--spacing-xxl) 0;
  opacity: 0;
  transition: transform var(--transition-normal), opacity 0.4s ease-in-out;
}

.logo.logo-loaded {
  opacity: 1;
}

.logo:hover {
  transform: scale(1.02);
}

/* Features Section */
.features {
  display: grid;
  gap: var(--spacing-xxl);
  margin: var(--spacing-xxl) auto;
  width: 100%;
  max-width: var(--container-max-width);
}

.feature {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  align-items: center;
  padding: var(--spacing-xl);
  background: var(--color-bg-tertiary);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

.feature::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.02) 50%, transparent 100%);
  opacity: 0;
  transition: opacity var(--transition-normal);
}

.feature:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.feature:hover::before {
  opacity: 1;
}

.feature-reverse {
  grid-template-columns: 1fr 1fr;
}

.feature-reverse .feature-content {
  order: 2;
}

.feature-reverse .feature-image {
  order: 1;
}

.feature-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  text-align: left;
}

.feature-title {
  font-size: var(--font-size-xl);
  margin-bottom: var(--spacing-md);
  background: var(--color-gradient-secondary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
}

.feature1 .feature-title {
  background: linear-gradient(135deg, #34daff, #00b7ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.feature2 .feature-title {
  background: linear-gradient(135deg, #8e97fa, #b8a9ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.feature3 .feature-title {
  background: linear-gradient(135deg, #fe5ea1, #ff8ec7);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.feature-description {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  line-height: 1.6;
}

.feature-image {
  position: relative;
}

.feature-image img {
  width: 100%;
  height: auto;
  transition: transform var(--transition-normal);
}

.feature-image:hover img {
  transform: scale(1.02);
}

/* Staff Section */
.staff-section {
  margin: var(--spacing-xxl) 0;
  text-align: center;
  width: 100%;
}

.staff-title {
  font-size: var(--font-size-xl);
  margin-bottom: var(--spacing-xl);
  background: var(--color-gradient-primary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.staff-grid {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: calc(var(--spacing-xl) + 4px);
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}

.staff-card {
  --staff-accent: #8e97fa;
  --staff-accent-soft: rgba(142, 151, 250, 0.26);
  --staff-accent-soft-2: rgba(142, 151, 250, 0.18);
  background: linear-gradient(155deg, rgba(26, 30, 41, 0.98) 0%, rgba(16, 20, 30, 0.98) 100%);
  padding: var(--spacing-xl);
  padding-top: calc(var(--spacing-xl) + 22px);
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow:
    0 24px 34px rgba(0, 0, 0, 0.36),
    0 8px 12px rgba(0, 0, 0, 0.18);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  text-align: center;
  width: 320px;
  flex-shrink: 0;
  isolation: isolate;
  transform: rotate(var(--staff-tilt, 0deg));
}

.staff-card::before {
  content: '';
  position: absolute;
  width: 180px;
  height: 180px;
  top: -78px;
  right: -68px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--staff-accent-soft) 0%, rgba(255, 255, 255, 0) 70%);
  filter: blur(2px);
  transition: transform var(--transition-normal), opacity var(--transition-normal);
}

.staff-card::after {
  content: '';
  position: absolute;
  width: 150px;
  height: 150px;
  bottom: -82px;
  left: -64px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--staff-accent-soft-2) 0%, rgba(255, 255, 255, 0) 70%);
  opacity: 0.75;
  transition: opacity var(--transition-normal), transform var(--transition-normal);
}

.staff-card:hover {
  transform: translateY(-10px) rotate(0deg);
  border-color: rgba(255, 255, 255, 0.24);
  box-shadow:
    0 30px 44px rgba(0, 0, 0, 0.4),
    0 12px 16px rgba(0, 0, 0, 0.2);
}

.staff-card:hover::before {
  transform: scale(1.08);
}

.staff-card:hover::after {
  opacity: 1;
  transform: scale(1.08);
}

.staff-avatar {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  margin: 0 auto var(--spacing-lg) auto;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.88);
  box-shadow: 0 14px 20px rgba(0, 0, 0, 0.28);
  transition: all var(--transition-normal);
  position: relative;
  z-index: 1;
}

.staff-avatar:hover {
  transform: scale(1.05);
}

.staff-name {
  font-size: clamp(1.2rem, 3vw, 1.45rem);
  margin-bottom: 8px;
  font-weight: 700;
  letter-spacing: 0.015em;
  color: #f8fbff;
  position: relative;
  z-index: 1;
}

.staff-role {
  color: rgba(236, 242, 255, 0.92);
  font-size: 0.74rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0;
  display: inline-block;
  padding: 7px 11px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1;
}

.staff-card--developer {
  --staff-accent: #34daff;
  --staff-accent-soft: rgba(52, 218, 255, 0.32);
  --staff-accent-soft-2: rgba(52, 218, 255, 0.2);
  --staff-tilt: -1.35deg;
  background: linear-gradient(160deg, rgba(17, 34, 44, 0.98) 0%, rgba(15, 21, 31, 0.98) 100%);
}

.staff-card--designer {
  --staff-accent: #a882ff;
  --staff-accent-soft: rgba(168, 130, 255, 0.32);
  --staff-accent-soft-2: rgba(168, 130, 255, 0.22);
  --staff-tilt: 1.35deg;
  background: linear-gradient(160deg, rgba(45, 30, 66, 0.98) 0%, rgba(21, 20, 36, 0.98) 100%);
}

.staff-card--developer .staff-avatar {
  border-color: rgba(84, 230, 255, 0.98);
}

.staff-card--designer .staff-avatar {
  border-color: rgba(188, 154, 255, 0.98);
}

.staff-card--developer .staff-role {
  background: rgba(52, 218, 255, 0.22);
  border-color: rgba(52, 218, 255, 0.36);
}

.staff-card--designer .staff-role {
  background: rgba(168, 130, 255, 0.22);
  border-color: rgba(168, 130, 255, 0.36);
}

/* Patreon Supporters Section */
.supporters-section {
  margin: var(--spacing-xxl) 0;
  text-align: center;
  width: 100%;
  padding: var(--spacing-xxl) var(--spacing-xl);
  background: linear-gradient(180deg, 
    rgba(255, 66, 77, 0.03) 0%, 
    rgba(255, 66, 77, 0.08) 50%, 
    rgba(255, 66, 77, 0.03) 100%);
  border-radius: var(--border-radius-xl);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 66, 77, 0.15);
}

.supporters-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 66, 77, 0.5), transparent);
}

.supporters-section::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 66, 77, 0.5), transparent);
}

/* Header */
.supporters-header {
  margin-bottom: var(--spacing-xl);
}

.supporters-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto var(--spacing-md);
  background: linear-gradient(135deg, #ff424d, #ff8080);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(255, 66, 77, 0.4);
  animation: heartbeat 2s ease-in-out infinite;
}

.supporters-icon svg {
  width: 28px;
  height: 28px;
  fill: white;
}

@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.supporters-title {
  font-size: clamp(2rem, 5vw, 3rem);
  background: linear-gradient(135deg, #ff424d, #ff8080, #ffb3b3);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: var(--spacing-sm);
  animation: gradientShift 4s ease-in-out infinite;
  font-weight: 700;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.supporters-description {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  max-width: 500px;
  margin: 0 auto;
}

/* Supporters Grid */
.supporters-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-lg);
  margin: var(--spacing-xl) auto;
  max-width: 800px;
  width: 100%;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  padding: 0 var(--spacing-md);
}

.supporters-grid.fading {
  opacity: 0;
}

/* Supporter Card */
.supporter-card {
  --supporter-hover-border: rgba(255, 66, 77, 0.3);
  --supporter-top-bar-start: #ff424d;
  --supporter-top-bar-end: #ff8080;
  background: rgba(255, 255, 255, 0.03);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg) var(--spacing-md);
  flex: 0 1 220px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: cardFadeIn 0.6s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  overflow: hidden;
}

.supporter-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--supporter-top-bar-start), var(--supporter-top-bar-end));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.supporter-card:hover::before {
  opacity: 1;
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.supporter-card:hover {
  transform: translateY(-6px);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--supporter-hover-border);
}

.supporter-card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
}

/* Avatar */
.supporter-avatar-wrapper {
  position: relative;
  margin-bottom: var(--spacing-xs);
}

.supporter-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff424d, #ff8080);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid rgba(255, 255, 255, 0.15);
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.supporter-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: inherit;
  filter: blur(15px);
  opacity: 0.4;
  z-index: 0;
  transition: opacity 0.3s ease, transform 0.35s ease, filter 0.35s ease;
}

.supporter-card:hover .supporter-glow {
  opacity: 0.6;
}

.supporter-avatar::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.2) 50%, transparent 60%);
  transform: translateX(-100%);
  animation: avatarShine 3s ease-in-out infinite;
}

@keyframes avatarShine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.supporter-initial {
  color: white;
  font-weight: 700;
  font-size: 1.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.supporter-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

/* Supporter Info */
.supporter-info {
  text-align: center;
}

.supporter-name {
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 var(--spacing-xs);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

/* Tier Badges */
.supporter-tier-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tier-icon {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

/* Tier-specific styles */
.tier-premium {
  --supporter-hover-border: rgba(255, 215, 0, 0.45);
  --supporter-top-bar-start: #ffd700;
  --supporter-top-bar-end: #ffb347;
}

.tier-premium .supporter-avatar {
  border-color: #ffd700;
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.4);
}

.tier-premium .supporter-glow {
  background: linear-gradient(135deg, #ffd700, #ffb347);
}

.tier-premium .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 179, 71, 0.2));
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.3);
}

.tier-premium .tier-icon {
  background: linear-gradient(135deg, #ffd700, #ffb347);
  box-shadow: 0 0 6px rgba(255, 215, 0, 0.6);
}

.tier-early {
  --supporter-hover-border: rgba(168, 85, 247, 0.45);
  --supporter-top-bar-start: #a855f7;
  --supporter-top-bar-end: #c084fc;
}

.tier-early .supporter-avatar {
  border-color: #a855f7;
  box-shadow: 0 0 20px rgba(168, 85, 247, 0.4);
}

.tier-early .supporter-glow {
  background: linear-gradient(135deg, #a855f7, #c084fc);
}

.tier-early .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(192, 132, 252, 0.2));
  color: #c084fc;
  border: 1px solid rgba(168, 85, 247, 0.3);
}

.tier-early .tier-icon {
  background: linear-gradient(135deg, #a855f7, #c084fc);
  box-shadow: 0 0 6px rgba(168, 85, 247, 0.6);
}

.tier-confetti {
  --supporter-hover-border: rgba(16, 197, 242, 0.45);
  --supporter-top-bar-start: #10c5f2;
  --supporter-top-bar-end: #61defe;
}

.tier-confetti .supporter-avatar {
  border-color: #10c5f2;
  box-shadow: 0 0 12px rgba(16, 197, 242, 0.22);
}

.tier-confetti .supporter-glow {
  background: linear-gradient(135deg, #10c5f2, #61defe);
  opacity: 0.35;
}

.tier-confetti .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(16, 197, 242, 0.14), rgba(97, 222, 254, 0.12));
  color: #7de6ff;
  border: 1px solid rgba(16, 197, 242, 0.28);
}

.tier-confetti .tier-icon {
  background: linear-gradient(135deg, #10c5f2, #61defe);
  box-shadow: 0 0 5px rgba(16, 197, 242, 0.45);
}

.supporter-card.tier-partygoer {
  --supporter-hover-border: rgba(155, 170, 241, 0.6);
  --supporter-top-bar-start: #7289da;
  --supporter-top-bar-end: #9baaf1;
  border-color: rgba(114, 137, 218, 0.28);
  background: linear-gradient(155deg, rgba(114, 137, 218, 0.16), rgba(29, 35, 57, 0.55) 45%, rgba(18, 21, 34, 0.75));
}

.tier-partygoer .supporter-avatar {
  border-color: #7289da;
  box-shadow: 0 0 18px rgba(114, 137, 218, 0.36);
}

.tier-partygoer .supporter-glow {
  background: linear-gradient(135deg, #7289da, #9baaf1);
  opacity: 0.48;
}

.tier-partygoer .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(114, 137, 218, 0.26), rgba(155, 170, 241, 0.2));
  color: #b8c5ff;
  border: 1px solid rgba(114, 137, 218, 0.52);
}

.tier-partygoer .tier-icon {
  background: linear-gradient(135deg, #7289da, #9baaf1);
  box-shadow: 0 0 9px rgba(114, 137, 218, 0.72);
}

.supporter-card.tier-garden-keeper {
  --supporter-hover-border: rgba(255, 188, 140, 0.9);
  border: 1px solid rgba(255, 78, 29, 0.55);
  background:
    radial-gradient(circle at 14% 16%, rgba(255, 165, 126, 0.24) 0%, rgba(255, 165, 126, 0) 36%),
    radial-gradient(circle at 84% 84%, rgba(255, 95, 55, 0.2) 0%, rgba(255, 95, 55, 0) 40%),
    linear-gradient(165deg, rgba(24, 27, 19, 0.97), rgba(19, 14, 12, 0.94));
  box-shadow: 0 0 30px rgba(255, 78, 29, 0.28), 0 16px 36px rgba(0, 0, 0, 0.48);
  isolation: isolate;
}

.supporter-card.tier-garden-keeper::before {
  opacity: 1;
  height: 4px;
  background: linear-gradient(90deg, #ff4e1d, #ffbc8c, #ff4e1d);
  background-size: 220% 100%;
  animation: gardenAccentShift 4s linear infinite;
}

.supporter-card.tier-garden-keeper::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background:
    radial-gradient(circle at 12% 22%, rgba(255, 78, 29, 0.24) 0%, transparent 12%),
    radial-gradient(circle at 84% 18%, rgba(255, 170, 132, 0.2) 0%, transparent 10%),
    radial-gradient(circle at 72% 82%, rgba(255, 78, 29, 0.18) 0%, transparent 11%);
  filter: blur(0.8px);
  transform-origin: center;
  animation: gardenBloomPulse 5s ease-in-out infinite, gardenFlaresDrift 9s ease-in-out infinite;
  z-index: 0;
}

.tier-garden-keeper .supporter-card-inner {
  position: relative;
  z-index: 2;
}

.tier-garden-keeper .supporter-avatar {
  border-color: #ff4e1d;
  box-shadow: 0 0 24px rgba(255, 78, 29, 0.5), 0 0 0 2px rgba(255, 188, 140, 0.28);
  position: relative;
}

.tier-garden-keeper .supporter-avatar::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid rgba(255, 188, 140, 0.74);
  border-right-color: rgba(255, 78, 29, 0.95);
  border-left-color: rgba(255, 78, 29, 0.45);
  animation: gardenRingDrift 5s linear infinite;
}

.tier-garden-keeper .supporter-glow {
  background: radial-gradient(circle at center, rgba(255, 78, 29, 0.9), rgba(255, 188, 140, 0.12) 72%);
  opacity: 0.7;
}

.tier-garden-keeper .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(255, 78, 29, 0.34), rgba(255, 188, 140, 0.2));
  color: #ffe2d4;
  border: 1px solid rgba(255, 78, 29, 0.65);
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(255, 78, 29, 0.35);
}

.tier-garden-keeper .tier-icon {
  background: linear-gradient(135deg, #ff4e1d, #ffc29c);
  box-shadow: 0 0 11px rgba(255, 78, 29, 0.8);
}

.supporter-card.tier-garden-keeper:hover {
  transform: translateY(-10px) scale(1.02);
  border-color: rgba(255, 188, 140, 0.9);
  box-shadow: 0 0 40px rgba(255, 78, 29, 0.42), 0 22px 48px rgba(0, 0, 0, 0.56);
}

.supporter-card.tier-partygoer:hover {
  transform: translateY(-7px);
  border-color: rgba(155, 170, 241, 0.6);
  box-shadow: 0 0 24px rgba(114, 137, 218, 0.25), 0 16px 36px rgba(0, 0, 0, 0.4);
}

.supporter-card.tier-founder {
  --supporter-hover-border: rgba(136, 255, 215, 0.8);
  border: 1px solid rgba(16, 245, 160, 0.55);
  overflow: hidden;
  isolation: isolate;
  background:
    linear-gradient(120deg, rgba(16, 245, 160, 0.2), rgba(16, 245, 160, 0) 32%),
    linear-gradient(160deg, rgba(10, 20, 24, 0.96), rgba(7, 14, 18, 0.92));
  box-shadow:
    0 0 0 1px rgba(16, 245, 160, 0.28),
    0 0 34px rgba(16, 245, 160, 0.32),
    0 0 70px rgba(16, 245, 160, 0.14),
    0 14px 36px rgba(0, 0, 0, 0.42);
}

.supporter-card.tier-founder::before {
  opacity: 1;
  height: 5px;
  background: linear-gradient(90deg, #10f5a0, #d8ffee, #10f5a0);
  background-size: 200% 100%;
  animation: founderAccentShift 2.6s linear infinite;
}

.supporter-card.tier-founder::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 14px;
  border: 1px solid rgba(16, 245, 160, 0.3);
  pointer-events: none;
  box-shadow: inset 0 0 30px rgba(16, 245, 160, 0.1);
}

.tier-founder .supporter-card-inner {
  position: relative;
}

.tier-founder .supporter-avatar {
  border-color: #10f5a0;
  box-shadow: 0 0 20px rgba(16, 245, 160, 0.55), 0 0 0 2px rgba(16, 245, 160, 0.3);
  position: relative;
}

.tier-founder .supporter-avatar::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid rgba(16, 245, 160, 0.45);
  border-top-color: rgba(216, 255, 238, 0.95);
  animation: founderHaloSpin 4s linear infinite;
}

.tier-founder .supporter-glow {
  background: radial-gradient(circle at center, rgba(16, 245, 160, 0.9) 0%, rgba(16, 245, 160, 0.15) 70%);
  width: 150%;
  height: 150%;
  opacity: 0.38;
  transform: translate(-50%, -50%) scale(1.02);
  filter: blur(15px);
  animation: founderGlowPulse 3.2s ease-in-out infinite;
}

.supporter-card.tier-founder:hover .supporter-glow {
  opacity: 0.72;
  transform: translate(-50%, -50%) scale(1.14);
  filter: blur(18px);
}

.tier-founder .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(16, 245, 160, 0.35), rgba(136, 255, 215, 0.2));
  color: #10f5a0;
  border: 1px solid rgba(16, 245, 160, 0.62);
  text-shadow: 0 0 12px rgba(16, 245, 160, 0.4);
  letter-spacing: 1px;
}

.tier-founder .tier-icon {
  box-shadow: 0 0 12px rgba(16, 245, 160, 0.85);
}

.supporter-card.tier-founder:hover {
  transform: translateY(-9px);
  border-color: rgba(136, 255, 215, 0.8);
  box-shadow:
    0 0 0 1px rgba(136, 255, 215, 0.44),
    0 0 38px rgba(16, 245, 160, 0.4),
    0 0 72px rgba(16, 245, 160, 0.16),
    0 20px 42px rgba(0, 0, 0, 0.52);
}

.tier-icon-crown {
  background: linear-gradient(135deg, #10f5a0, #88ffd7);
  width: 11px;
  height: 11px;
  border-radius: 2px;
  clip-path: polygon(0% 100%, 10% 40%, 30% 60%, 45% 20%, 60% 58%, 78% 34%, 100% 100%);
}

.tier-icon-diamond {
  border-radius: 2px;
  transform: rotate(45deg);
}

.tier-icon-bloom {
  width: 12px;
  height: 12px;
  border-radius: 0;
  clip-path: polygon(50% 0%, 70% 6%, 87% 18%, 100% 36%, 95% 50%, 80% 54%, 64% 52%, 60% 60%, 60% 82%, 70% 94%, 30% 94%, 40% 82%, 40% 60%, 36% 52%, 20% 54%, 5% 50%, 0% 36%, 13% 18%, 30% 6%);
}

@keyframes founderAccentShift {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

@keyframes founderHaloSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes founderGlowPulse {
  0%, 100% {
    opacity: 0.34;
  }
  50% {
    opacity: 0.46;
  }
}

@keyframes gardenAccentShift {
  0% { background-position: 0% 50%; }
  100% { background-position: 220% 50%; }
}

@keyframes gardenRingDrift {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes gardenBloomPulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}

@keyframes gardenFlaresDrift {
  0%, 100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  25% {
    transform: translate3d(1.5%, -1%, 0) scale(1.03);
  }
  50% {
    transform: translate3d(-1.8%, 1.2%, 0) scale(0.98);
  }
  75% {
    transform: translate3d(1.2%, 1.8%, 0) scale(1.02);
  }
}

.supporter-card.tier-legend {
  border: 2px solid;
  border-image: linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3) 1;
  background: linear-gradient(
    135deg,
    rgba(255, 107, 107, 0.15) 0%,
    rgba(254, 202, 87, 0.12) 25%,
    rgba(72, 219, 251, 0.15) 50%,
    rgba(255, 159, 243, 0.12) 75%,
    rgba(107, 255, 160, 0.12) 100%
  );
  background-size: 200% 200%;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 0 20px rgba(255, 159, 243, 0.25),
    0 0 40px rgba(72, 219, 251, 0.15),
    0 8px 32px rgba(0, 0, 0, 0.4);
  filter: hue-rotate(0deg);
  animation: 
    legendFloat 4s ease-in-out infinite, 
    legendBackgroundShift 8s ease-in-out infinite,
    legendColorShift 12s linear infinite;
}

/* Shimmer sweep effect */
.supporter-card.tier-legend::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.08) 20%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.08) 80%,
    transparent 100%
  );
  animation: legendShine 6s linear infinite;
  pointer-events: none;
  z-index: 0;
}

/* Dynamic glow pulse that moves */
.supporter-card.tier-legend::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 150px 100px at 50% 30%, rgba(255, 159, 243, 0.15) 0%, transparent 60%);
  animation: legendGlowShift 6s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
}

.tier-legend .supporter-card-inner {
  position: relative;
  z-index: 1;
}

.tier-legend .supporter-avatar {
  border-color: #ff9ff3;
  box-shadow: 0 0 25px rgba(255, 159, 243, 0.5), 0 0 50px rgba(72, 219, 251, 0.25);
  animation: legendAvatarPulse 3s ease-in-out infinite;
  filter: hue-rotate(0deg);
  animation: legendAvatarPulse 3s ease-in-out infinite, legendColorShift 12s linear infinite;
}

.tier-legend .supporter-glow {
  background: linear-gradient(135deg, #ff6b6b, #48dbfb);
  opacity: 0.6;
  animation: legendGlowBreathe 4s ease-in-out infinite;
}

.tier-legend .supporter-tier-badge {
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.25), rgba(72, 219, 251, 0.25));
  color: #ffb3ff;
  border: 1.5px solid rgba(255, 159, 243, 0.5);
  box-shadow: 0 0 15px rgba(255, 159, 243, 0.3);
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  animation: legendBadgeGlow 3s ease-in-out infinite, legendColorShift 12s linear infinite;
  filter: hue-rotate(0deg);
}

.tier-legend .tier-icon {
  background: linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb);
  box-shadow: 0 0 12px rgba(255, 159, 243, 0.7);
  animation: legendIconGlow 2.5s ease-in-out infinite, legendColorShift 12s linear infinite;
  filter: hue-rotate(0deg);
}

.supporter-card.tier-legend:hover {
  transform: translateY(-8px);
  border-image: linear-gradient(135deg, #ff9ff3, #48dbfb, #feca57, #ff6b6b) 1;
  box-shadow: 
    0 0 30px rgba(255, 159, 243, 0.4),
    0 0 60px rgba(72, 219, 251, 0.25),
    0 15px 45px rgba(0, 0, 0, 0.5);
  animation: legendFloat 4s ease-in-out infinite, legendBackgroundShift 8s ease-in-out infinite, legendColorShift 12s linear infinite;
}

.supporter-card.tier-legend:hover::before {
  animation: legendShine 2s ease-in-out infinite;
}

@keyframes legendFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
}

@keyframes legendBackgroundShift {
  0% { background-position: 0% 50%; }
  25% { background-position: 100% 0%; }
  50% { background-position: 100% 100%; }
  75% { background-position: 0% 100%; }
  100% { background-position: 0% 50%; }
}

@keyframes legendColorShift {
  0% { filter: hue-rotate(0deg); }
  16.67% { filter: hue-rotate(30deg); }
  33.33% { filter: hue-rotate(60deg); }
  50% { filter: hue-rotate(90deg); }
  66.67% { filter: hue-rotate(240deg); }
  83.33% { filter: hue-rotate(300deg); }
  100% { filter: hue-rotate(360deg); }
}

@keyframes legendShine {
  0% { transform: translateX(0%); }
  100% { transform: translateX(200%); }
}

@keyframes legendGlowShift {
  0%, 100% { background-position: 50% 30%; }
  25% { background-position: 70% 40%; }
  50% { background-position: 50% 60%; }
  75% { background-position: 30% 40%; }
}

@keyframes legendAvatarPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes legendGlowBreathe {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
}

@keyframes legendBadgeGlow {
  0%, 100% { box-shadow: 0 0 15px rgba(255, 159, 243, 0.3); }
  50% { box-shadow: 0 0 25px rgba(255, 159, 243, 0.5); }
}

@keyframes legendIconGlow {
  0%, 100% { box-shadow: 0 0 12px rgba(255, 159, 243, 0.7); transform: scale(1); }
  50% { box-shadow: 0 0 20px rgba(255, 159, 243, 0.9); transform: scale(1.08); }
}

@keyframes legendHoverGlow {
  0%, 100% { box-shadow: 0 0 30px rgba(255, 159, 243, 0.4), 0 0 60px rgba(72, 219, 251, 0.25), 0 15px 45px rgba(0, 0, 0, 0.5); }
  50% { box-shadow: 0 0 40px rgba(255, 159, 243, 0.5), 0 0 80px rgba(72, 219, 251, 0.35), 0 15px 45px rgba(0, 0, 0, 0.5); }
}

/* CTA Section */
.patreon-cta {
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-xl);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.cta-text {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  margin: 0 auto var(--spacing-md) auto;
  font-weight: 600;
}

.support-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.support-subtitle {
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
  margin: var(--spacing-sm) auto 0 auto;
  max-width: 500px;
}

.patreon-button {
  background: linear-gradient(135deg, #ff424d, #ff6b73);
  color: white;
  padding: var(--spacing-md) var(--spacing-xl);
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: var(--font-size-md);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(255, 66, 77, 0.4);
  position: relative;
  overflow: hidden;
}

.patreon-button:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 12px 40px rgba(255, 66, 77, 0.5);
  background: linear-gradient(135deg, #ff5a63, #ff8080);
}

.patreon-button:active {
  transform: translateY(-1px) scale(1);
}

.button-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: buttonShine 3s ease-in-out infinite;
}

@keyframes buttonShine {
  0% { left: -100%; }
  50%, 100% { left: 100%; }
}

.patreon-icon {
  width: 20px;
  height: 20px;
  fill: white;
}

.topgg-support-button {
  background: linear-gradient(135deg, #FF396B, #1D1B26) !important;
  box-shadow: 0 8px 30px rgba(255, 57, 107, 0.4) !important;
}

.topgg-support-button:hover {
  background: linear-gradient(135deg, #FF5080, #FF396B) !important;
  box-shadow: 0 12px 40px rgba(255, 57, 107, 0.5) !important;
}

.topgg-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .feature {
    padding: var(--spacing-lg);
  }
}

@media (max-width: 768px) {
  .feature {
    grid-template-columns: 1fr !important;
    text-align: center;
    padding: var(--spacing-md);
  }
  
  .feature-reverse .feature-content {
    order: 1;
  }
  
  .feature-reverse .feature-image {
    order: 2;
  }
  
  .supporters-section {
    padding: var(--spacing-lg) var(--spacing-sm);
    margin: var(--spacing-lg) 0;
  }
  
  .supporters-icon {
    width: 50px;
    height: 50px;
  }
  
  .supporters-icon svg {
    width: 24px;
    height: 24px;
  }
  
  .supporters-title {
    font-size: clamp(1.75rem, 6vw, 2.5rem);
  }
  
  .supporters-description {
    font-size: var(--font-size-sm);
  }
  
  .supporters-grid {
    gap: var(--spacing-md);
    padding: 0 var(--spacing-sm);
  }
  
  .supporter-card {
    padding: var(--spacing-md);
    flex-basis: 200px;
  }
  
  .supporter-avatar {
    width: 60px;
    height: 60px;
  }
  
  .supporter-initial {
    font-size: 1.25rem;
  }
  
  .supporter-name {
    font-size: 0.85rem;
    max-width: 100px;
  }
  
  .supporter-tier-badge {
    padding: 3px 8px;
    font-size: 0.65rem;
  }
  
  .staff-avatar {
    width: 100px;
    height: 100px;
  }
  
  .staff-section {
    text-align: center;
  }
  
  .staff-grid {
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--spacing-lg);
  }
  
  .staff-card {
    --staff-tilt: 0deg;
    width: 100%;
    max-width: 320px;
    padding: var(--spacing-lg);
    padding-top: calc(var(--spacing-lg) + 18px);
  }
}

@media (max-width: 480px) {
  .supporters-section {
    padding: var(--spacing-md) var(--spacing-xs);
  }
  
  .supporters-icon {
    width: 45px;
    height: 45px;
  }
  
  .supporters-icon svg {
    width: 20px;
    height: 20px;
  }

  .supporters-title {
    font-size: clamp(1.5rem, 6vw, 2rem);
  }
  
  .supporters-description {
    font-size: 0.9rem;
  }

  .supporters-grid {
    gap: var(--spacing-sm);
  }
  
  .supporter-card {
    padding: var(--spacing-sm);
    flex-basis: 160px;
  }

  .supporter-avatar {
    width: 50px;
    height: 50px;
  }

  .supporter-initial {
    font-size: 1.1rem;
  }

  .supporter-name {
    font-size: 0.75rem;
    max-width: 80px;
  }

  .supporter-tier-badge {
    padding: 2px 6px;
    font-size: 0.6rem;
  }
  
  .patreon-button {
    padding: var(--spacing-sm) var(--spacing-lg);
    font-size: var(--font-size-sm);
  }
  
  .support-buttons {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }
  
  .support-subtitle {
    font-size: 0.75rem;
  }
  
  .cta-text {
    font-size: 0.85rem;
  }
  
  .feature {
    padding: var(--spacing-sm);
    text-align: center;
  }
  
  .feature-title {
    font-size: var(--font-size-lg);
  }
  
  .feature-description {
    font-size: var(--font-size-sm);
  }
  
  .staff-avatar {
    width: 80px;
    height: 80px;
  }
  
  .staff-name {
    font-size: var(--font-size-md);
  }
  
  .staff-role {
    font-size: var(--font-size-sm);
  }
  
  .staff-section {
    text-align: center;
  }

  .staff-card {
    max-width: 300px;
    padding-top: calc(var(--spacing-lg) + 14px);
  }
}

/* Animation utilities */
.fade-in-up {
  opacity: 1; /* Make visible by default */
  transform: translateY(0);
  transition: all 0.6s ease-out;
}

.fade-in-up.will-animate {
  opacity: 0;
  transform: translateY(30px);
}

.fade-in-up.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.fade-in {
  opacity: 1; /* Make visible by default */
  transition: opacity 0.6s ease-out;
}

.fade-in.will-animate {
  opacity: 0;
}

.fade-in.animate-in {
  opacity: 1;
}

/* Staggered animations for grid items */
.feature, .staff-card, .supporter-card {
  opacity: 1; /* Make visible by default */
  transform: translateY(0);
  transition: all 0.4s ease-out;
}

.feature.will-animate, .staff-card.will-animate, .supporter-card.will-animate {
  opacity: 0;
  transform: translateY(20px);
}

.feature.animate-in, .staff-card.animate-in, .supporter-card.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.feature.animate-in:hover, .feature.will-animate:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

/* Enhanced hover effects */
.feature {
  transition: all var(--transition-normal), opacity 0.4s ease-out, transform 0.4s ease-out;
}

.staff-card {
  transition: all var(--transition-normal), opacity 0.4s ease-out, transform 0.4s ease-out;
}

.supporter-card {
  transition: all 0.4s ease, opacity 0.4s ease-out, transform 0.4s ease-out;
}

/* Loading state */
.loading {
  opacity: 0.7;
  pointer-events: none;
}

/* Pulse animation for loading states */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.pulse {
  animation: pulse 2s ease-in-out infinite;
}

/* Smooth transitions for all interactive elements */
button, a, .nav-item, .supporter-card, .staff-card, .feature {
  transition: all var(--transition-normal);
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .fade-in-up,
  .fade-in,
  .feature,
  .staff-card,
  .supporter-card {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
  
  .gradientPulse,
  .gradientShift,
  .shimmer,
  .avatarShine,
  .pulse {
    animation: none !important;
  }
}

/* Focus states for accessibility */
.patreon-button:focus-visible,
.supporter-card:focus-visible {
  outline: 2px solid var(--color-text-primary);
  outline-offset: 2px;
}
</style>